import React, { useEffect, useState } from 'react';
import { Plus, Pencil, ToggleLeft, ToggleRight, X } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const COST_UNITS = [
  { value: 'per_query', label: 'Per Query' },
  { value: 'per_message', label: 'Per Message' },
  { value: 'per_minute', label: 'Per Minute' },
  { value: 'per_page', label: 'Per Page' },
  { value: 'per_gb_month', label: 'Per GB/Month' },
];

const unitLabel = (u) => COST_UNITS.find(c => c.value === u)?.label || u;

const emptyForm = { name: '', slug: '', description: '', base_cost_unit: 'per_query', provider_cost_per_unit: '', default_markup_pct: '30', current_provider: '' };

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add, object = edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchServices = async () => {
    try {
      const { data } = await api.get('/platform/services');
      setServices(data.services);
    } catch (err) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (svc) => {
    setEditing(svc);
    setForm({
      name: svc.name,
      slug: svc.slug,
      description: svc.description || '',
      base_cost_unit: svc.base_cost_unit,
      provider_cost_per_unit: svc.provider_cost_per_unit || '',
      default_markup_pct: svc.default_markup_pct || '',
      current_provider: svc.current_provider || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        provider_cost_per_unit: parseFloat(form.provider_cost_per_unit) || 0,
        default_markup_pct: parseFloat(form.default_markup_pct) || 30,
      };
      if (editing) {
        await api.put(`/platform/services/${editing.id}`, payload);
        toast.success('Service updated');
      } else {
        // Auto-generate slug from name if empty
        if (!payload.slug) payload.slug = payload.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
        await api.post('/platform/services', payload);
        toast.success('Service added');
      }
      setModalOpen(false);
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (svc) => {
    try {
      await api.put(`/platform/services/${svc.id}`, { is_active: !svc.is_active });
      setServices(prev => prev.map(s => s.id === svc.id ? { ...s, is_active: !s.is_active } : s));
      toast.success(`${svc.name} ${!svc.is_active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error('Failed to update service');
    }
  };

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Platform Services</h1>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors">
          <Plus className="w-4 h-4" /> Add Service
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Service</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Provider</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Cost Unit</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Cost/Unit</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Markup %</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Tenants</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>
            ) : services.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">No services configured</td></tr>
            ) : services.map(svc => (
              <tr key={svc.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{svc.name}</p>
                  <p className="text-xs text-gray-500">{svc.slug}</p>
                </td>
                <td className="px-4 py-3 text-gray-700">{svc.current_provider || '-'}</td>
                <td className="px-4 py-3 text-gray-700">{unitLabel(svc.base_cost_unit)}</td>
                <td className="px-4 py-3 text-right font-mono text-gray-700">${parseFloat(svc.provider_cost_per_unit).toFixed(4)}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-700">{parseFloat(svc.default_markup_pct).toFixed(0)}%</td>
                <td className="px-4 py-3 text-center text-gray-700">{svc.enabled_tenants || 0}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${svc.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {svc.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(svc)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Edit">
                      <Pencil className="w-4 h-4 text-gray-500" />
                    </button>
                    <button onClick={() => toggleActive(svc)} className="p-1.5 rounded-lg hover:bg-gray-100" title={svc.is_active ? 'Deactivate' : 'Activate'}>
                      {svc.is_active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{editing ? 'Edit Service' : 'Add Service'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input value={form.name} onChange={(e) => { setForm(f => ({ ...f, name: e.target.value, ...(editing ? {} : { slug: autoSlug(e.target.value) }) })); }}
                    required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                  <input value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
                    required disabled={!!editing} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-100" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Unit *</label>
                  <select value={form.base_cost_unit} onChange={(e) => setForm(f => ({ ...f, base_cost_unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                    {COST_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <input value={form.current_provider} onChange={(e) => setForm(f => ({ ...f, current_provider: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider Cost/Unit ($)</label>
                  <input type="number" step="0.000001" value={form.provider_cost_per_unit} onChange={(e) => setForm(f => ({ ...f, provider_cost_per_unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Markup %</label>
                  <input type="number" step="0.01" value={form.default_markup_pct} onChange={(e) => setForm(f => ({ ...f, default_markup_pct: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Add Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
